import { useEffect, useRef, useState } from "react";
import { ChevronRight, SlidersHorizontal } from "lucide-react";
import { useLineCustomFieldsExpandStore } from "@/store/lineCustomFieldsExpandStore";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  getCustomFieldNames,
  type CustomFieldsSchema,
  type CustomFieldValues,
} from "@forgepoint/shared";
import { CustomFieldsSection } from "./CustomFieldsSection";
import { ReadOnlyValue } from "./ReadOnlyValue";

/** Props for the LineCustomFieldsCollapsible component. */
interface LineCustomFieldsCollapsibleProps {
  /** Stable id of the line row, used to track open state across rows. */
  lineId: string;
  schema: CustomFieldsSchema;
  values: CustomFieldValues;
  readOnly: boolean;
  onChange: (fieldName: string, value: CustomFieldValues[string]) => void;
}

/**
 * Determines whether a custom field value counts as filled in.
 * @param value - The raw field value
 * @returns True when the value holds meaningful content
 */
function isFilled(value: CustomFieldValues[string]): boolean {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  return !Array.isArray(value) || value.length > 0;
}

/**
 * Renders a line item's custom fields collapsed by default behind a summary
 * trigger, so quotes with many lines stay short. Expanding reveals the full
 * editable field form. The collapsed trigger summarizes filled values inline.
 * @param schema - The custom fields JSON Schema defining available fields
 * @param values - Current field values keyed by field name
 * @param readOnly - Whether the section is in read-only display mode
 * @param onChange - Callback when a field value changes
 */
export function LineCustomFieldsCollapsible({
  lineId,
  schema,
  values,
  readOnly,
  onChange,
}: LineCustomFieldsCollapsibleProps) {
  const [open, setOpen] = useState(false);
  const expandVersion = useLineCustomFieldsExpandStore((s) => s.version);
  const expandAll = useLineCustomFieldsExpandStore((s) => s.expanded);
  const registerOpen = useLineCustomFieldsExpandStore((s) => s.setOpen);
  const lastVersion = useRef(expandVersion);

  useEffect(() => {
    if (expandVersion !== lastVersion.current) {
      lastVersion.current = expandVersion;
      setOpen(expandAll);
    }
  }, [expandVersion, expandAll]);

  useEffect(() => {
    registerOpen(lineId, open);
    return () => registerOpen(lineId, false);
  }, [lineId, open, registerOpen]);

  const filledNames = getCustomFieldNames(schema).filter((name) =>
    isFilled(values[name]),
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mx-2 mt-0.5">
      <CollapsibleTrigger className="group flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground">
        <ChevronRight
          className={cn(
            "h-3 w-3 shrink-0 transition-transform duration-200 ease-out",
            open && "rotate-90",
          )}
        />
        <SlidersHorizontal className="h-3 w-3 shrink-0 transition-transform duration-200 ease-out group-hover:-rotate-6" />
        {filledNames.length === 0 ? (
          <span className="italic text-muted-foreground/70">
            {readOnly ? "No custom fields" : "Add custom fields"}
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
            {filledNames.map((name) => {
              const prop = schema.properties[name];
              if (!prop) {
                return null;
              }
              return (
                <span key={name} className="flex items-center gap-1">
                  <span className="text-muted-foreground/70">{name}:</span>
                  <span className="text-foreground">
                    <ReadOnlyValue
                      value={values[name] ?? null}
                      format={prop.format}
                      type={prop.type[0]}
                      inline={false}
                    />
                  </span>
                </span>
              );
            })}
          </span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden pt-1 data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <CustomFieldsSection
          schema={schema}
          values={values}
          readOnly={readOnly}
          onChange={onChange}
          compact
          layout="rows"
        />
      </CollapsibleContent>
    </Collapsible>
  );
}
